import axios from "axios";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const TOKEN_KEY = "karams_admin_token";

export const getToken = () => localStorage.getItem(TOKEN_KEY);
export const setToken = (t) => localStorage.setItem(TOKEN_KEY, t);
export const clearToken = () => localStorage.removeItem(TOKEN_KEY);

const authHeaders = () => ({ headers: { Authorization: `Bearer ${getToken()}` } });

export const getContent = async () => (await axios.get(`${API}/content`, { timeout: 20000 })).data;
export const getDraftContent = async () => (await axios.get(`${API}/content/draft`, authHeaders())).data;
export const loginApi = async (email, password) => (await axios.post(`${API}/auth/login`, { email, password })).data;
export const meApi = async () => (await axios.get(`${API}/auth/me`, authHeaders())).data;
export const updateContentApi = async (data) => (await axios.put(`${API}/content`, { data }, authHeaders())).data;
export const publishContentApi = async () => (await axios.post(`${API}/content/publish`, {}, authHeaders())).data;
export const resetContentApi = async () => (await axios.post(`${API}/content/reset`, {}, authHeaders())).data;
export const listBackups = async () => (await axios.get(`${API}/content/backups`, authHeaders())).data;
export const restoreBackup = async (id) => (await axios.post(`${API}/content/restore/${id}`, {}, authHeaders())).data;
export const createBackup = async (label) => (await axios.post(`${API}/content/backup`, { label }, authHeaders())).data;
export const exportContent = async () => (await axios.get(`${API}/content/export`, authHeaders())).data;
export const exportBackup = async (id) => (await axios.get(`${API}/content/backups/${id}/export`, authHeaders())).data;
export const importContent = async (data) => (await axios.post(`${API}/content/import`, { data }, authHeaders())).data;
export const getBackupSettings = async () => (await axios.get(`${API}/settings/backup`, authHeaders())).data;
export const putBackupSettings = async (frequency) => (await axios.put(`${API}/settings/backup`, { frequency }, authHeaders())).data;
export const submitContact = async (form) => (await axios.post(`${API}/contact`, form)).data;
export const createSubmission = async (payload) => (await axios.post(`${API}/submissions`, payload)).data;
export const listSubmissions = async (type) => (await axios.get(`${API}/submissions${type ? `?type=${type}` : ""}`, authHeaders())).data;
export const deleteSubmission = async (id) => (await axios.delete(`${API}/submissions/${id}`, authHeaders())).data;
export const updateSubmission = async (id, patch) => (await axios.patch(`${API}/submissions/${id}`, patch, authHeaders())).data;
export const replySubmission = async (id, message, subject = "", retryId = null) =>
  (await axios.post(`${API}/submissions/${id}/reply`, { message, subject, ...(retryId ? { retryId } : {}) }, authHeaders())).data;
export const bulkSubmissions = async (ids, action) => (await axios.post(`${API}/submissions/bulk`, { ids, action }, authHeaders())).data;
export const unreadCount = async () => (await axios.get(`${API}/submissions/unread-count`, authHeaders())).data;
export const getEmailSettings = async () => (await axios.get(`${API}/settings/email`, authHeaders())).data;
export const putEmailSettings = async (data) => (await axios.put(`${API}/settings/email`, data, authHeaders())).data;
export const listContacts = async () => (await axios.get(`${API}/contact`, authHeaders())).data;
export const aiWrite = async (payload) => (await axios.post(`${API}/ai/write`, payload, authHeaders())).data;

export const uploadImage = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/upload`, fd, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" } });
  return data;
};

export const uploadDocument = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/upload/document`, fd, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" } });
  return data;
};

export const uploadZip = async (file) => {
  const fd = new FormData();
  fd.append("file", file);
  const { data } = await axios.post(`${API}/upload/zip`, fd, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" } });
  return data;
};

const VIDEO_CHUNK_SIZE = 4 * 1024 * 1024; // 4MB — under ingress limits

export const uploadVideo = async (file, onProgress) => {
  const { data: init } = await axios.post(`${API}/upload/video/init`, {}, authHeaders());
  const uploadId = init.uploadId;
  const total = Math.ceil(file.size / VIDEO_CHUNK_SIZE);
  for (let index = 0; index < total; index++) {
    const blob = file.slice(index * VIDEO_CHUNK_SIZE, (index + 1) * VIDEO_CHUNK_SIZE);
    const fd = new FormData();
    fd.append("uploadId", uploadId);
    fd.append("index", index);
    fd.append("chunk", blob, `chunk-${index}`);
    await axios.post(`${API}/upload/video/chunk`, fd, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" } });
    if (onProgress) onProgress(Math.round(((index + 1) / total) * 100));
  }
  const fd = new FormData();
  fd.append("uploadId", uploadId);
  fd.append("filename", file.name);
  const { data } = await axios.post(`${API}/upload/video/complete`, fd, { headers: { Authorization: `Bearer ${getToken()}`, "Content-Type": "multipart/form-data" } });
  return data;
};

export const analyticsSummary = async (range) => (await axios.get(`${API}/analytics/summary?range=${range}`, authHeaders())).data;
export const analyticsRealtime = async () => (await axios.get(`${API}/analytics/realtime`, authHeaders())).data;

export const listUsers = async () => (await axios.get(`${API}/users`, authHeaders())).data;
export const createUser = async (payload) => (await axios.post(`${API}/users`, payload, authHeaders())).data;
export const updateUser = async (id, payload) => (await axios.put(`${API}/users/${id}`, payload, authHeaders())).data;
export const deleteUser = async (id) => (await axios.delete(`${API}/users/${id}`, authHeaders())).data;
export const changePasswordApi = async (payload) => (await axios.post(`${API}/auth/change-password`, payload, authHeaders())).data;

// ---- Newsletter ----
export const subscribeNewsletter = async (email, consent = true) => (await axios.post(`${API}/newsletter/subscribe`, { email, consent })).data;
export const listSubscribers = async (search = "", status = "") => (await axios.get(`${API}/newsletter/subscribers`, { ...authHeaders(), params: { search, status } })).data;
export const deleteSubscriber = async (id) => (await axios.delete(`${API}/newsletter/subscribers/${id}`, authHeaders())).data;
export const listNewsletters = async () => (await axios.get(`${API}/newsletters`, authHeaders())).data;
export const createNewsletter = async (payload) => (await axios.post(`${API}/newsletters`, payload, authHeaders())).data;
export const updateNewsletter = async (id, payload) => (await axios.put(`${API}/newsletters/${id}`, payload, authHeaders())).data;
export const deleteNewsletter = async (id) => (await axios.delete(`${API}/newsletters/${id}`, authHeaders())).data;
export const testNewsletter = async (id, email) => (await axios.post(`${API}/newsletters/${id}/test`, { email }, authHeaders())).data;
export const sendNewsletter = async (id) => (await axios.post(`${API}/newsletters/${id}/send`, {}, authHeaders())).data;

// ---- Maintenance mode ----
export const getMaintenance = async () => (await axios.get(`${API}/maintenance`)).data;
export const updateMaintenance = async (payload) => (await axios.put(`${API}/maintenance`, payload, authHeaders())).data;

// ---- Annonces immersives (Mode A vidéo / Mode B pop-up) ----
export const listAnnouncements = async () => (await axios.get(`${API}/announcements`, authHeaders())).data;
export const previewAnnouncement = async (data) => (await axios.post(`${API}/announcements/preview`, data, authHeaders())).data;

// ---- Candidatures (formations / inscriptions) ----
export const getFormationCandidacies = async (refId, sessionId) => {
  const q = sessionId ? `?sessionId=${encodeURIComponent(sessionId)}` : "";
  return (await axios.get(`${API}/formations/${refId}/candidacies${q}`, authHeaders())).data;
};
export const decideCandidacy = async (id, action, note = "") => (await axios.post(`${API}/submissions/${id}/decision`, { action, note }, authHeaders())).data;
export const reclassifySubmission = async (id, target) => (await axios.post(`${API}/submissions/${id}/reclassify`, { target }, authHeaders())).data;
export const previewDecisionEmail = async (id, action) => (await axios.post(`${API}/submissions/${id}/decision-email`, { action }, authHeaders())).data;
export const getAttendance = async (refId, sessionId) => (await axios.get(`${API}/formations/${refId}/sessions/${sessionId}/attendance`, authHeaders())).data;
export const setAttendance = async (id, state) => (await axios.patch(`${API}/submissions/${id}/attendance`, { state }, authHeaders())).data;
export const getRecapSettings = async () => (await axios.get(`${API}/settings/session-recap`, authHeaders())).data;
export const putRecapSettings = async (data) => (await axios.put(`${API}/settings/session-recap`, data, authHeaders())).data;
export const resendRecap = async (refId, sessionId) => (await axios.post(`${API}/formations/${refId}/sessions/${sessionId}/send-recap`, {}, authHeaders())).data;
export const createAnnouncement = async (payload) => (await axios.post(`${API}/announcements`, payload, authHeaders())).data;
export const updateAnnouncement = async (id, payload) => (await axios.put(`${API}/announcements/${id}`, payload, authHeaders())).data;
export const deleteAnnouncement = async (id) => (await axios.delete(`${API}/announcements/${id}`, authHeaders())).data;
export const getActiveAnnouncements = async () => (await axios.get(`${API}/announcements/active`)).data;
export const videoDiagnostics = async () => (await axios.get(`${API}/admin/video-diagnostics`, authHeaders())).data;

// ---- Modèles de réponses (messagerie admin) ----
export const listReplyTemplates = async () => (await axios.get(`${API}/reply-templates`, authHeaders())).data;
export const createReplyTemplate = async (payload) => (await axios.post(`${API}/reply-templates`, payload, authHeaders())).data;
export const updateReplyTemplate = async (id, payload) => (await axios.put(`${API}/reply-templates/${id}`, payload, authHeaders())).data;
export const deleteReplyTemplate = async (id) => (await axios.delete(`${API}/reply-templates/${id}`, authHeaders())).data;
